import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CandlestickChart, TrendingUp, BarChart3, BookOpen, Shield,
  ChevronDown, ChevronUp, ArrowRight
} from 'lucide-react'
import AnimatedSection from '../components/AnimatedSection'
import SectionHeading from '../components/SectionHeading'

const categories = [
  { id: 'all', label: 'All Topics' },
  { id: 'basics', label: 'Basics' },
  { id: 'technical', label: 'Technical Analysis' },
  { id: 'patterns', label: 'Patterns' },
  { id: 'psychology', label: 'Psychology' },
]

const topics = [
  {
    id: 1,
    category: 'basics',
    icon: BookOpen,
    title: 'Stock Market Fundamentals',
    description: 'Understand how stock markets work, types of orders, market participants, and the basics of buying and selling securities.',
    points: ['What is a stock exchange?', 'Types of orders: Market, Limit, Stop-loss', 'Bull vs Bear markets', 'Market capitalization explained'],
  },
  {
    id: 2,
    category: 'technical',
    icon: BarChart3,
    title: 'Technical Indicators',
    description: 'Learn to use RSI, MACD, Moving Averages, Bollinger Bands, and other essential indicators for market timing.',
    points: ['RSI - Relative Strength Index', 'MACD - Moving Average Convergence Divergence', 'Simple & Exponential Moving Averages', 'Bollinger Bands strategy'],
  },
  {
    id: 3,
    category: 'patterns',
    icon: CandlestickChart,
    title: 'Candlestick Patterns',
    description: 'Master Doji, Hammer, Engulfing, Morning Star, and other powerful candlestick formations for trade entry and exit signals.',
    points: ['Single candle patterns: Doji, Hammer, Shooting Star', 'Double candle patterns: Engulfing, Harami', 'Triple candle patterns: Morning Star, Evening Star', 'Pattern confirmation techniques'],
  },
  {
    id: 4,
    category: 'technical',
    icon: TrendingUp,
    title: 'Support & Resistance',
    description: 'Identify key price levels that act as barriers. Learn to draw trendlines and use them for strategic entry and exit points.',
    points: ['Drawing accurate trendlines', 'Identifying support zones', 'Resistance breakout strategies', 'Role reversal concept'],
  },
  {
    id: 5,
    category: 'patterns',
    icon: CandlestickChart,
    title: 'Chart Patterns',
    description: 'Recognize Head & Shoulders, Triangles, Flags, Pennants, and Cup & Handle patterns for high-probability setups.',
    points: ['Reversal patterns: Head & Shoulders, Double Top', 'Continuation patterns: Flags, Pennants', 'Triangle patterns: Ascending, Descending, Symmetrical', 'Volume confirmation in patterns'],
  },
  {
    id: 6,
    category: 'psychology',
    icon: Shield,
    title: 'Risk Management',
    description: 'Protect your capital with position sizing, stop-loss rules, risk-reward ratios, and portfolio diversification strategies.',
    points: ['Position sizing formulas', 'Setting effective stop-losses', 'Risk-reward ratio: 1:2, 1:3', 'Portfolio diversification'],
  },
  {
    id: 7,
    category: 'psychology',
    icon: TrendingUp,
    title: 'Trading Psychology',
    description: 'Develop emotional discipline, overcome FOMO and fear, and build a mindset that supports consistent, profitable trading.',
    points: ['Overcoming fear and greed', 'FOMO management techniques', 'Building a trading journal', 'Developing patience and discipline'],
  },
  {
    id: 8,
    category: 'basics',
    icon: BookOpen,
    title: 'Fundamental Analysis',
    description: 'Evaluate company financials, read balance sheets, analyze P/E ratios, and understand economic indicators.',
    points: ['Reading financial statements', 'Key ratios: P/E, ROE, Debt-to-Equity', 'Economic indicators impact', 'Sector analysis basics'],
  },
]

function TopicCard({ topic }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <motion.div
      layout
      className="glass rounded-3xl overflow-hidden card-hover glow-border"
    >
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-brand-500/10 flex items-center justify-center shrink-0">
              <topic.icon size={20} className="text-brand-600 dark:text-brand-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">{topic.title}</h3>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{topic.description}</p>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 rounded-xl hover:bg-[var(--bg-secondary)] transition-colors shrink-0"
          >
            {expanded ? <ChevronUp size={18} className="text-[var(--text-muted)]" /> : <ChevronDown size={18} className="text-[var(--text-muted)]" />}
          </button>
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="pt-5 mt-5 border-t border-[var(--border-color)]">
                <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-3">What You Will Learn:</h4>
                <ul className="space-y-2">
                  {topic.points.map((point, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                      <ArrowRight size={14} className="text-brand-500 shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

export default function Learn() {
  const [activeCategory, setActiveCategory] = useState('all')

  const filtered = activeCategory === 'all'
    ? topics
    : topics.filter(t => t.category === activeCategory)

  return (
    <div className="min-h-screen pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 via-transparent to-brand-600/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase bg-brand-500/10 text-brand-600 dark:text-brand-400 mb-4"
          >
            Learning Center
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--text-primary)] mb-6 text-balance"
          >
            Master Trading <span className="gradient-text">Step by Step</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto text-balance"
          >
            Structured curriculum covering everything from stock market basics to advanced technical analysis.
            Click on any topic to explore what you will learn.
          </motion.p>
        </div>
      </section>

      {/* Filter */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.id
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/25'
                    : 'glass text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Topics Grid */}
      <section className="py-12 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div layout className="space-y-5">
            <AnimatePresence mode="popLayout">
              {filtered.map((topic) => (
                <motion.div
                  key={topic.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                >
                  <TopicCard topic={topic} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
